import type {
  CreateMatriculaUseCase,
  GetMatriculaUseCase,
  ListMatriculasByAlunoUseCase,
  ListMatriculasUseCase,
  TransitionMatriculaStatusUseCase,
} from '@/application/port/in/MatriculaUseCase';
import type {
  CreateMatriculaCommand,
  ListMatriculasQuery,
  TransitionMatriculaStatusCommand,
} from '@/application/port/in/dto/MatriculaCommand';
import type { MatriculaGateway } from '@/application/port/out/MatriculaGateway';
import type { Matricula, MatriculaStatus } from '@/domain/model/Matricula';
import { canTransitionMatricula } from '@/domain/model/Matricula';
import type { Page } from '@/domain/model/Page';

export class ListMatriculasUseCaseImpl implements ListMatriculasUseCase {
  constructor(private readonly gateway: MatriculaGateway) {}
  execute(query: ListMatriculasQuery): Promise<Page<Matricula>> {
    return this.gateway.list(query);
  }
}

export class GetMatriculaUseCaseImpl implements GetMatriculaUseCase {
  constructor(private readonly gateway: MatriculaGateway) {}
  execute(id: string): Promise<Matricula> {
    if (!id) return Promise.reject(new Error('ID da matrícula é obrigatório'));
    return this.gateway.getById(id);
  }
}

export class ListMatriculasByAlunoUseCaseImpl
  implements ListMatriculasByAlunoUseCase
{
  constructor(private readonly gateway: MatriculaGateway) {}
  execute(alunoId: string): Promise<Matricula[]> {
    if (!alunoId)
      return Promise.reject(new Error('ID do aluno é obrigatório'));
    return this.gateway.listByAluno(alunoId);
  }
}

export class CreateMatriculaUseCaseImpl implements CreateMatriculaUseCase {
  constructor(private readonly gateway: MatriculaGateway) {}
  execute(command: CreateMatriculaCommand): Promise<Matricula> {
    return this.gateway.create(command);
  }
}

export class TransitionMatriculaStatusUseCaseImpl
  implements TransitionMatriculaStatusUseCase
{
  constructor(
    private readonly gateway: MatriculaGateway,
    private readonly getById: (id: string) => Promise<Matricula>,
  ) {}

  async execute(command: TransitionMatriculaStatusCommand): Promise<Matricula> {
    const requiresMotivo: ReadonlyArray<MatriculaStatus> = [
      'TRANCADA',
      'CANCELADA',
    ];
    if (requiresMotivo.includes(command.status) && !command.motivo?.trim()) {
      return Promise.reject(
        new Error('Motivo é obrigatório ao trancar ou cancelar a matrícula.'),
      );
    }
    const current = await this.getById(command.id);
    if (!canTransitionMatricula(current.status, command.status)) {
      return Promise.reject(
        new Error(
          `Transição inválida: ${current.status} → ${command.status}.`,
        ),
      );
    }
    return this.gateway.transitionStatus(command);
  }
}
